import type { ReactNode } from 'react';
import { useState } from 'react';
import { Image, Platform, Text, TextInput, TouchableOpacity, View, ActivityIndicator } from 'react-native';
import { AppIcon, type AppIconName } from './AppIcon';
import { PRODUCT_NAME } from './legal';
import { getNotificationIconStyle, type NotificationFeedItem } from '../notifications';
import { useTabBarPlacement } from '../platform/use-tab-bar-placement';
import type { Tab, Translation } from '../types';
import { colors, styles } from '../theme';

export function NavigationHeader({
  title,
  onBack,
  notificationCount = 0,
  onPressNotifications,
}: {
  title: string;
  onBack: () => boolean;
  notificationCount?: number;
  onPressNotifications?: () => void;
}) {
  return (
    <View style={styles.navHeader}>
      <TouchableOpacity accessibilityLabel="Go back" onPress={onBack} style={styles.navBack}>
        <Text style={styles.navBackText}>‹</Text>
      </TouchableOpacity>
      <Text style={styles.navTitle} numberOfLines={1}>{title}</Text>
      {onPressNotifications ? (
        <NotificationBellButton count={notificationCount} onPress={onPressNotifications} />
      ) : (
        <View style={styles.navSpacer} />
      )}
    </View>
  );
}

export function BottomTabs({ t, active, onTabPress }: { t: Translation; active: Tab; onTabPress: (tab: Tab) => void }) {
  const { bottom, paddingBottom } = useTabBarPlacement();
  const tabs: { id: Tab; label: string; icon: AppIconName }[] = [
    { id: 'home', label: t.home, icon: 'home' },
    { id: 'rank', label: t.rank, icon: 'trending-up' },
    { id: 'play', label: t.play, icon: 'plus-circle' },
    { id: 'profile', label: t.profile, icon: 'user' },
    { id: 'settings', label: t.settings, icon: 'settings' },
  ];

  return (
    <View
      style={[
        styles.tabsWrap,
        Platform.OS === 'web' ? { position: 'fixed' } as object : null,
        { bottom, paddingBottom },
      ]}
    >
      <View
        style={
          Platform.OS === 'web'
            ? ({
                borderRadius: 30,
                boxShadow: '0 12px 40px rgba(17, 17, 17, 0.14)',
              } as object)
            : null
        }
      >
        <View
          style={[
            styles.tabs,
            Platform.OS === 'web'
              ? ({
                  backgroundColor: 'rgba(255, 255, 255, 0.58)',
                  borderColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(40px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                  boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 1), inset 0 -0.5px 0 rgba(17, 17, 17, 0.06)',
                } as object)
              : null,
          ]}
        >
          {tabs.map(item => {
            const isActive = active === item.id;
            const iconColor = isActive ? colors.interactive : '#2F2F2C';

            return (
              <TouchableOpacity
                key={item.id}
                style={[styles.tabItem, isActive && styles.tabItemActive]}
                onPress={() => onTabPress(item.id)}
              >
                <View style={[styles.tabIconWrap, isActive && styles.tabIconWrapActive]}>
                  <AppIcon name={item.icon} size={24} color={iconColor} strokeWidth={isActive ? 1.75 : 2} />
                </View>
                <Text style={[styles.tabText, isActive && styles.activeTab]}>{item.label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </View>
  );
}

export function ClubhouseWordmark() {
  return (
    <Text
      accessibilityRole="header"
      accessibilityLabel={PRODUCT_NAME}
      style={[styles.pageTitle, styles.screenTitleText]}
    >
      <Text style={styles.clubhouseWordmarkClub}>CLUB</Text>
      <Text>HOUSE</Text>
    </Text>
  );
}

export function ScreenTitleRow({
  title,
  brand,
  notificationCount = 0,
  onPressNotifications,
  accessory,
}: {
  title?: string;
  brand?: boolean;
  notificationCount?: number;
  onPressNotifications?: () => void;
  accessory?: ReactNode;
}) {
  return (
    <View style={styles.screenTitleRow}>
      {brand ? <ClubhouseWordmark /> : <Text style={[styles.pageTitle, styles.screenTitleText]} numberOfLines={1}>{title}</Text>}
      <View style={styles.screenTitleActions}>
        {accessory}
        {onPressNotifications ? (
          <NotificationBellButton count={notificationCount} onPress={onPressNotifications} />
        ) : null}
      </View>
    </View>
  );
}

export function Header({
  notificationCount = 0,
  onPressNotifications,
}: {
  title?: string;
  notificationCount?: number;
  onPressNotifications?: () => void;
}) {
  return (
    <ScreenTitleRow
      brand
      notificationCount={notificationCount}
      onPressNotifications={onPressNotifications}
    />
  );
}

export function NotificationBellButton({ count, onPress }: { count: number; onPress: () => void }) {
  const badgeLabel = count > 99 ? '99+' : String(count);

  return (
    <TouchableOpacity
      accessibilityLabel="Notifications"
      accessibilityHint="Open notifications"
      onPress={onPress}
      style={styles.bellButton}
      activeOpacity={0.88}
    >
      <View style={styles.bellIcon}>
        <View style={styles.bellKnob} />
        <View style={styles.bellCup} />
        <View style={styles.bellRim} />
        <View style={styles.bellClapper} />
      </View>
      {count > 0 && (
        <View style={styles.bellBadge}>
          <Text style={styles.bellBadgeText}>{badgeLabel}</Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

export function SectionHeader({ title, action, onPress }: { title: string; action?: string; onPress?: () => void }) {
  return (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {action ? (
        <TouchableOpacity disabled={!onPress} onPress={onPress}>
          <Text style={styles.link}>{action}</Text>
        </TouchableOpacity>
      ) : (
        <View />
      )}
    </View>
  );
}

export function PillRow({ items, active, setActive }: { items: string[]; active: string; setActive?: (item: string) => void }) {
  return <View style={styles.pillRow}>{items.map(item => <TouchableOpacity key={item} onPress={() => setActive?.(item)}><Text style={[styles.filterPill, item === active && styles.activePill]}>{item}</Text></TouchableOpacity>)}</View>;
}

export function QuickAction({
  label,
  detail,
  icon,
  onPress,
  disabled,
}: {
  label: string;
  detail: string;
  icon: AppIconName;
  onPress: () => void;
  disabled?: boolean;
}) {
  const iconColor = disabled ? colors.disabled : colors.interactiveStrong;

  return (
    <TouchableOpacity disabled={disabled} style={[styles.quickCard, disabled && styles.disabledQuickCard]} onPress={onPress}>
      <View style={styles.quickIconWrap}>
        <AppIcon name={icon} size={22} color={iconColor} />
      </View>
      <Text style={styles.cardTitle}>{label}</Text>
      <Text style={[styles.muted, disabled && styles.soonText]}>{detail}</Text>
    </TouchableOpacity>
  );
}

export function NotificationRow({
  item,
  onPress,
  onDismiss,
  footer,
}: {
  item: NotificationFeedItem;
  onPress: () => void;
  onDismiss?: () => void;
  footer?: ReactNode;
}) {
  const tone =
    item.kind === 'friendRequest'
      ? 'friend'
      : getNotificationIconStyle(item.linkType, item.title);

  const iconWrapStyle = {
    friend: styles.notificationIconWrapFriend,
    matchplay: styles.notificationIconWrapMatchplay,
    result: styles.notificationIconWrapResult,
    cancelled: styles.notificationIconWrapCancelled,
    duel: styles.notificationIconWrapDuel,
    booking: styles.notificationIconWrapBooking,
    join: styles.notificationIconWrapJoin,
    default: null,
  }[tone];

  return (
    <View style={[styles.notificationRow, item.read ? styles.notificationRowRead : styles.notificationRowUnread]}>
      <View style={styles.notificationRowMain}>
        <TouchableOpacity
          style={styles.notificationRowContent}
          onPress={onPress}
          disabled={!onPress}
          activeOpacity={0.88}
        >
          <View style={[styles.notificationIconWrap, iconWrapStyle]}>
            <Text style={styles.notificationIcon}>{item.icon}</Text>
          </View>
          <View style={styles.notificationContent}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {!item.read ? <View style={styles.notificationUnreadDot} /> : null}
              <Text style={[styles.notificationTitle, item.read && styles.notificationTitleRead]} numberOfLines={2}>
                {item.title}
              </Text>
            </View>
            <Text style={styles.notificationBody}>{item.body}</Text>
          </View>
        </TouchableOpacity>
        {!item.read && onDismiss ? (
          <TouchableOpacity
            accessibilityLabel="Mark as read"
            onPress={onDismiss}
            style={styles.notificationDismissButton}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          >
            <Text style={styles.notificationDismissText}>×</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      {footer}
    </View>
  );
}

export function Avatar({
  initials,
  photoUrl,
  small,
  large,
  activeMatch,
  muted,
  uploading,
  onPress,
}: {
  initials: string;
  photoUrl?: string | null;
  small?: boolean;
  large?: boolean;
  activeMatch?: boolean;
  muted?: boolean;
  uploading?: boolean;
  onPress?: () => void;
}) {
  const avatar = (
    <View
      style={[
        styles.avatar,
        small && styles.smallAvatar,
        large && styles.largeAvatar,
        activeMatch && styles.activeMatchAvatar,
        activeMatch && muted && styles.activeMatchAvatarMuted,
      ]}
    >
      {photoUrl ? (
        <Image source={{ uri: photoUrl }} style={styles.avatarImage} resizeMode="cover" />
      ) : (
        <Text
          style={[
            styles.avatarText,
            small && styles.smallAvatarText,
            large && styles.largeAvatarText,
            activeMatch && styles.activeMatchAvatarText,
            activeMatch && muted && styles.activeMatchAvatarMutedText,
          ]}
        >
          {initials}
        </Text>
      )}
      {uploading ? (
        <View style={styles.avatarUploadingMask} pointerEvents="none">
          <ActivityIndicator color="#FFFFFF" />
        </View>
      ) : null}
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        onPress={onPress}
        disabled={uploading}
        activeOpacity={0.85}
        accessibilityRole="button"
        accessibilityLabel="Change profile photo"
        accessibilityState={{ busy: Boolean(uploading), disabled: Boolean(uploading) }}
      >
        {avatar}
      </TouchableOpacity>
    );
  }

  return avatar;
}

export function OutlinePill({ label }: { label: string }) {
  return (
    <View style={styles.profileOutlinePill}>
      <Text style={styles.profileOutlinePillText}>{label}</Text>
    </View>
  );
}

export function PremiumPill({ label }: { label: string }) {
  return (
    <View style={styles.profileGreenPillWrap}>
      <Text style={styles.profileGreenPillText}>{label}</Text>
    </View>
  );
}

export function StatGrid({ stats }: { stats: { value: string; label: string; highlight?: 'win' | 'loss'; compact?: boolean; numberOfLines?: number; valueFontSize?: number }[] }) {
  return (
    <View style={styles.statGrid}>
      {stats.map(item => (
        <View key={item.label} style={[styles.stat, item.compact && styles.statCompact]}>
          <Text
            style={[
              styles.statValue,
              item.compact && styles.statValueCompact,
              item.highlight === 'win' && styles.statValueAccent,
              item.highlight === 'loss' && styles.statValueAccentLoss,
              item.valueFontSize ? { fontSize: item.valueFontSize, lineHeight: item.valueFontSize + 4 } : null,
            ]}
            numberOfLines={item.numberOfLines ?? 1}
            adjustsFontSizeToFit={item.compact}
            minimumFontScale={0.7}
          >
            {item.value}
          </Text>
          <Text style={styles.statLabel}>{item.label}</Text>
        </View>
      ))}
    </View>
  );
}

export function Performance({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.performanceRow}>
      <Text style={styles.performanceLabel}>{label}</Text>
      <Text style={styles.performanceValue}>{value}</Text>
    </View>
  );
}

export function ScoreStepper({
  label,
  value,
  setValue,
  hint,
}: {
  label: string;
  value: number | null;
  setValue: (value: number | null) => void;
  hint?: string;
}) {
  return (
    <View style={styles.scoreStepper}>
      <Text style={styles.muted}>{label}</Text>
      <View style={styles.row}>
        <TouchableOpacity onPress={() => setValue(value == null ? null : value <= 1 ? null : value - 1)}>
          <Text style={styles.roundControl}>-</Text>
        </TouchableOpacity>
        <Text style={[styles.scoreNumber, value == null && styles.scoreNumberEmpty]}>{value == null ? '–' : value}</Text>
        <TouchableOpacity onPress={() => setValue(value == null ? 1 : Math.min(20, value + 1))}>
          <Text style={styles.roundControl}>+</Text>
        </TouchableOpacity>
      </View>
      {hint ? <Text style={styles.scoreFromOpponent}>{hint}</Text> : null}
    </View>
  );
}

export function AuthField({
  label,
  value,
  onChangeText,
  secureTextEntry,
  valid,
  keyboardType,
  autoCapitalize,
  showLabel,
  hideLabel,
  onSubmitEditing,
}: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  secureTextEntry?: boolean;
  valid?: boolean;
  keyboardType?: 'default' | 'email-address';
  autoCapitalize?: 'none' | 'words';
  showLabel?: string;
  hideLabel?: string;
  onSubmitEditing?: () => void;
}) {
  const [visible, setVisible] = useState(false);
  const isPassword = Boolean(secureTextEntry);
  const showText = showLabel ?? 'Show';
  const hideText = hideLabel ?? 'Hide';

  return (
    <View>
      <Text style={styles.inputLabel}>{label}</Text>
      <View style={styles.authInputWrap}>
        <TextInput
          autoCapitalize={autoCapitalize ?? (label === 'EMAIL' ? 'none' : 'words')}
          keyboardType={keyboardType ?? (label === 'EMAIL' ? 'email-address' : 'default')}
          onChangeText={onChangeText}
          placeholderTextColor={colors.muted}
          secureTextEntry={isPassword && !visible}
          onSubmitEditing={onSubmitEditing}
          style={[styles.fakeInput, styles.authInput, valid && styles.authInputValid]}
          value={value}
        />
        <View style={styles.authInputActions}>
          {valid ? <Text style={styles.authInputCheck}>✓</Text> : null}
          {isPassword ? (
            <TouchableOpacity accessibilityRole="button" onPress={() => setVisible(current => !current)}>
              <Text style={styles.authInputToggle}>{visible ? hideText : showText}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  );
}

export function LabeledInput({ label, value, muted, onChangeText, secureTextEntry }: { label: string; value: string; muted?: boolean; onChangeText?: (value: string) => void; secureTextEntry?: boolean }) {
  if (onChangeText) {
    return (
      <View>
        <Text style={styles.inputLabel}>{label}</Text>
        <TextInput
          autoCapitalize={label === 'EMAIL' ? 'none' : 'words'}
          keyboardType={label === 'EMAIL' ? 'email-address' : 'default'}
          onChangeText={onChangeText}
          placeholderTextColor={colors.muted}
          secureTextEntry={secureTextEntry}
          style={[styles.fakeInput, muted && styles.mutedInput]}
          value={value}
        />
      </View>
    );
  }
  return <View><Text style={styles.inputLabel}>{label}</Text><Text style={[styles.fakeInput, muted && styles.mutedInput]}>{value}</Text></View>;
}

export function SettingsRow({ label, value }: { label: string; value: ReactNode }) {
  return <View style={styles.settingsRow}><Text style={styles.cardTitle}>{label}</Text>{typeof value === 'string' ? <Text style={styles.muted}>{value}</Text> : value}</View>;
}
